import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { FeedbackAnimation } from '@/components/TransactionModule/components/TransactionToast/animations/FeedbackAnimation';
import {
  ToastContainer,
  ToastContent,
  ToastTitle,
  useAutoDismiss,
} from '@/components/TransactionToast/ToastComponents';

import { HYPERCORE_CHAIN_ID, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

type PerpsToastProps = {
  feedback: 'success' | 'error';
  title: string;
  description: ReactNode;
  /** Drawn inside the feedback badge; defaults to the HyperCore logo. */
  icon?: ReactNode;
  toastId: string | number;
};

type ShowToastParams = Omit<PerpsToastProps, 'toastId' | 'feedback'>;

const showPerpsToast = (params: Omit<PerpsToastProps, 'toastId'>) =>
  toast.custom((toastId) => <PerpsToast {...params} toastId={toastId} />, {
    duration: Infinity,
    unstyled: true,
  });

/** Swap-toast-styled success notice for perps actions (funding, orders). */
export const showPerpsSuccessToast = (params: ShowToastParams) =>
  showPerpsToast({ ...params, feedback: 'success' });

export const showPerpsErrorToast = (params: ShowToastParams) =>
  showPerpsToast({ ...params, feedback: 'error' });

const PerpsToast = ({ feedback, title, description, icon, toastId }: PerpsToastProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const onDismiss = () => toast.dismiss(toastId);
  useAutoDismiss(isHovered, onDismiss, false);

  return (
    <ToastContainer onClose={onDismiss} onHoverChange={setIsHovered}>
      <FeedbackAnimation feedback={feedback} className="relative z-10 shrink-0">
        <div className="flex size-10 items-center justify-center" aria-hidden="true">
          {icon ?? (
            <ChainAvatar
              chainId={HYPERCORE_CHAIN_ID}
              alt={NETWORK_NAME_PER_CHAIN_ID[HYPERCORE_CHAIN_ID]}
              size={26}
            />
          )}
        </div>
      </FeedbackAnimation>
      <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-1">
        <ToastTitle>{title}</ToastTitle>
        <ToastContent>
          <div
            className={fontStyles({
              variant: 'LABEL_MD',
              class: 'flex flex-wrap items-center gap-1 text-token-gray-1600',
            })}
          >
            {description}
          </div>
        </ToastContent>
      </div>
    </ToastContainer>
  );
};
